import React from "react";
import { api } from "@/lib/api";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";

/**
 * Poll card rendered inline inside a channel MessageItem when the message
 * has a `poll` field. Shows the question, options with progress bars, and
 * lets the current user toggle a vote. Colors follow the caller's bubble
 * (`own` → orange-tinted, otherwise neutral).
 */
export default function PollCard({ msg, poll, currentUserId, own }) {
  const totalVotes = (poll.options || []).reduce(
    (n, o) => n + (o.votes?.length || 0),
    0
  );
  const closed = !!poll.closed;

  const vote = async (index) => {
    if (closed) return;
    try {
      await api.post(`/messages/${msg.id}/vote`, { option_index: index });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record vote");
    }
  };

  return (
    <div
      className={`mt-1 rounded-xl border p-3 w-72 shadow-sm ${
        own
          ? "border-orange-200 bg-orange-50 text-zinc-800"
          : "border-zinc-200 bg-white text-zinc-800"
      }`}
      data-testid={`poll-card-${msg.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-3.5 h-3.5 opacity-80"/>
        <span className="text-[10px] uppercase tracking-widest font-semibold opacity-80">
          Poll{poll.allow_multi ? " · Multi" : ""}{closed ? " · Closed" : ""}
        </span>
      </div>
      <div className="text-sm font-semibold mb-2 whitespace-pre-wrap break-words">{poll.question}</div>
      <div className="space-y-2">
        {(poll.options || []).map((opt, i) => {
          const count = opt.votes?.length || 0;
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const voted = !!(opt.votes || []).includes(currentUserId);
          return (
            <button
              // Option text is unique per poll (server enforces distinct
              // entries at create time) so it works as a stable React key
              // and survives reorders better than a raw index.
              key={`${msg.id}-opt-${opt.text}-${i}`}
              onClick={() => vote(i)}
              disabled={closed}
              className={`relative w-full text-left rounded-md border overflow-hidden transition border-zinc-200 hover:bg-zinc-50 ${
                voted ? "ring-1 ring-[var(--brand-accent)]" : ""
              } ${closed ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
              data-testid={`poll-option-btn-${msg.id}-${i}`}
            >
              <div
                className="absolute inset-y-0 left-0 bg-orange-100 transition-all"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
              <div className="relative flex items-center justify-between px-2.5 py-1.5 text-xs">
                <span className="flex items-center gap-1.5 min-w-0">
                  {voted && <span className="w-3 h-3 rounded-full bg-[var(--brand-accent)] shrink-0" aria-hidden/>}
                  <span className="truncate">{opt.text}</span>
                </span>
                <span className="ml-2 shrink-0 font-semibold tabular-nums">
                  {count} · {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <div className={`text-[10px] mt-2 text-zinc-500`}>
        {totalVotes === 0 ? "No votes yet" : `${totalVotes} vote${totalVotes === 1 ? "" : "s"}`}
      </div>
    </div>
  );
}
